import React from 'react';
import { Badge, Skeleton } from '@/components/ui';
import { CompanyCard } from './CompanyCard';
import type { Company, CompanySortField } from '@/types/companyManagement.types';

interface CompanyTableProps {
  companies: Company[];
  onEdit?: (company: Company) => void;
  onDelete?: (company: Company) => void;
  isLoading?: boolean;
  sortField?: CompanySortField;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: CompanySortField) => void;
}

const SortableHeader: React.FC<{
  field: CompanySortField;
  label: string;
  sortField?: CompanySortField;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: CompanySortField) => void;
  className?: string;
}> = ({ field, label, sortField, sortDirection, onSort, className = '' }) => {
  const isSorted = sortField === field;
  const ariaSort = isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';
  const isRight = className.includes('text-right');

  return (
    <th
      className={`px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300 md:px-6 md:py-3 ${className}`}
      aria-sort={onSort ? ariaSort : undefined}
    >
      {onSort ? (
        <button
          type="button"
          className={`flex w-full select-none items-center gap-1 rounded px-1 py-1 text-left uppercase tracking-wider hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-dark-600 ${isRight ? 'justify-end' : ''}`}
          onClick={() => onSort(field)}
          aria-label={`Sort by ${label}`}
        >
          <span>{label}</span>
          <div className="flex flex-col" aria-hidden="true">
            <svg
              className={`h-3 w-3 transition-colors ${
                isSorted && sortDirection === 'asc' ? 'text-primary-600' : 'text-gray-400'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" />
            </svg>
            <svg
              className={`-mt-1 h-3 w-3 transition-colors ${
                isSorted && sortDirection === 'desc' ? 'text-primary-600' : 'text-gray-400'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </div>
        </button>
      ) : (
        <span>{label}</span>
      )}
    </th>
  );
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
}

export const CompanyTable: React.FC<CompanyTableProps> = (props) => {
  const { companies, onEdit, onDelete, isLoading = false } = props;

  if (isLoading) {
    return (
      <>
        {/* Mobile loading skeleton — card shaped */}
        <div className="block space-y-2 md:hidden">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={`company-card-skeleton-${index}`}
              className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 dark:border-dark-700 dark:bg-dark-800"
            >
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 flex-shrink-0 rounded" />
                <Skeleton className="h-9 w-9 flex-shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-44" />
                  <Skeleton className="h-3 w-36" />
                  <div className="flex flex-wrap gap-1">
                    <Skeleton className="h-5 w-14 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop loading skeleton — table shaped */}
        <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800 md:block">
          <div className="space-y-1 p-4">
            <div className="grid grid-cols-12 gap-3 rounded-md bg-gray-50 p-3 dark:bg-dark-700">
              <Skeleton className="col-span-3 h-4" />
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="col-span-1 h-4" />
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="col-span-2 h-4" />
            </div>
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`company-table-skeleton-${index}`} className="grid grid-cols-12 gap-3 p-3">
                <Skeleton className="col-span-3 h-4" />
                <Skeleton className="col-span-2 h-4" />
                <Skeleton className="col-span-2 h-4" />
                <Skeleton className="col-span-1 h-6 w-16 rounded-full" />
                <Skeleton className="col-span-2 h-6 w-20 rounded-full" />
                <div className="col-span-2 flex justify-end gap-2">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-dark-700 dark:bg-dark-800">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 48 48"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16M3 21h18M9 21v4m6-4v4M21 21h18M27 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16m10 0v4m-4-4v4"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          No companies found
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Get started by creating a new company.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile — card list */}
      <div className="block space-y-2 md:hidden">
        {companies.map((company) => (
          <CompanyCard key={company.id} company={company} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>

      {/* Desktop — table (unchanged) */}
      <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800 md:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] table-fixed divide-y divide-gray-200 dark:divide-gray-700">
            <colgroup>
              {/* Plain length/% only — `min()` on <col> is ignored in many engines, so width never changed */}
              <col className="min-w-0" style={{ width: '11.5rem' }} />
              <col style={{ width: '6.25rem' }} />
              <col style={{ width: '6.25rem' }} />
              <col style={{ width: '4.75rem' }} />
              <col style={{ width: '5.5rem' }} />
              <col style={{ width: '6.25rem' }} />
              <col className="hidden min-w-0 lg:table-column" style={{ width: '11%' }} />
              <col className="hidden min-w-0 xl:table-column" style={{ width: '11%' }} />
              <col style={{ width: '4.75rem' }} />
            </colgroup>
            <thead className="bg-gray-50 dark:bg-dark-700">
              <tr>
                <SortableHeader
                  field="name"
                  label="Name"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="w-[11.5rem] max-w-[11.5rem]"
                />
                <SortableHeader
                  field="startDate"
                  label="Start Date"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="hidden !text-right sm:table-cell"
                />
                <SortableHeader
                  field="endDate"
                  label="End Date"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="hidden !text-right sm:table-cell"
                />
                <SortableHeader
                  field="status"
                  label="Status"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                />
                <SortableHeader
                  field="userCount"
                  label="Users"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="hidden !text-right sm:table-cell md:!px-2"
                />
                <SortableHeader
                  field="lastLoginAt"
                  label="Last Login"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="hidden !text-right sm:table-cell"
                />
                <SortableHeader
                  field="pools"
                  label="Pools"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="hidden lg:table-cell"
                />
                <SortableHeader
                  field="groups"
                  label="Roles"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="hidden xl:table-cell"
                />
                <th className="px-1.5 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300 md:py-3 md:pl-2 md:pr-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
              {companies.map((company) => (
                <tr key={company.id} className="group hover:bg-gray-50 dark:hover:bg-dark-700">
                  {/* Name */}
                  <td className="w-[11.5rem] min-w-0 max-w-[11.5rem] px-2 py-2 md:px-3 md:py-2.5">
                    <div className="flex min-w-0 items-center">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 md:h-9 md:w-9">
                        <span className="text-xs font-semibold text-primary-700 dark:text-primary-400 md:text-sm">
                          {company.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-2 min-w-0 md:ml-3">
                        <div className="whitespace-normal break-words text-sm font-medium leading-snug text-gray-900 dark:text-white">
                          {company.name}
                        </div>
                        <div className="truncate text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                          {formatDate(company.startDate)} – {formatDate(company.endDate)}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Start Date */}
                  <td className="hidden whitespace-nowrap px-2 py-2 text-right text-sm text-gray-700 dark:text-gray-300 sm:table-cell md:px-3 md:py-2.5">
                    {formatDate(company.startDate)}
                  </td>

                  {/* End Date */}
                  <td className="hidden whitespace-nowrap px-2 py-2 text-right text-sm text-gray-700 dark:text-gray-300 sm:table-cell md:px-3 md:py-2.5">
                    {formatDate(company.endDate)}
                  </td>

                  {/* Is Inactive */}
                  <td className="whitespace-nowrap px-2 py-2 md:px-3 md:py-2.5">
                    <Badge variant={company.isInactive ? 'error' : 'success'} size="sm">
                      {company.isInactive ? 'Inactive' : 'Active'}
                    </Badge>
                  </td>

                  {/* Users */}
                  <td className="hidden whitespace-nowrap px-2 py-2 text-right text-sm text-gray-700 dark:text-gray-300 sm:table-cell md:px-3 md:py-2.5">
                    {company.userCount}
                  </td>

                  {/* Last Login */}
                  <td className="hidden whitespace-nowrap px-2 py-2 text-right text-sm text-gray-700 dark:text-gray-300 sm:table-cell md:px-3 md:py-2.5">
                    {formatDateTime(company.lastLoginAt)}
                  </td>

                  {/* Pools */}
                  <td className="hidden min-w-0 px-2 py-2 align-top md:px-3 md:py-2.5 lg:table-cell">
                    {company.poolIds.length > 0 ? (
                      <div className="flex w-full min-w-0 max-w-full flex-col gap-1 text-sm text-gray-900 dark:text-white">
                        {company.poolIds.slice(0, 6).map((p) => (
                          <Badge
                            key={p.poolId}
                            variant="primary"
                            size="sm"
                            className="w-fit max-w-full whitespace-normal break-words text-left leading-snug"
                          >
                            {p.pool}
                          </Badge>
                        ))}
                        {company.poolIds.length > 6 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            +{company.poolIds.length - 6} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>

                  {/* Groups/Roles */}
                  <td className="hidden min-w-0 px-2 py-2 align-top md:px-3 md:py-2.5 xl:table-cell">
                    {company.groups.length > 0 ? (
                      <div className="flex w-full min-w-0 max-w-full flex-col gap-1 text-sm text-gray-900 dark:text-white">
                        {company.groups.slice(0, 6).map((g) => (
                          <Badge
                            key={g.code}
                            variant="default"
                            size="sm"
                            className="w-fit max-w-full whitespace-normal break-words text-left leading-snug"
                          >
                            {g.name}
                          </Badge>
                        ))}
                        {company.groups.length > 6 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            +{company.groups.length - 6} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="whitespace-nowrap px-1.5 py-2 text-right align-top text-sm font-medium md:px-2 md:py-2.5">
                    <div className="flex items-center justify-end gap-0.5">
                      {onEdit && (
                        <button
                          type="button"
                          onClick={() => onEdit(company)}
                          className="rounded p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-900 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300"
                          title="Edit company"
                          aria-label={`Edit ${company.name}`}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                      )}

                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(company)}
                          className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
                          title="Delete company"
                          aria-label={`Delete ${company.name}`}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
